import { useTranslation } from 'react-i18next';
import { Box, Icons, iconSize, Stack, Typography } from '@munifocus/ui';
import { useFileContactHistory } from '../../lib/front-desk-api';
import { ContactEntrySummary } from './ContactEntrySummary';

/**
 * Every front-desk contact about this file: those linked to it, and those
 * about the same property or owner that nobody linked. Hidden entirely where
 * the municipality has the front desk off, or the reader cannot load it.
 */
export function ContactHistoryPanel({ fileId }: { fileId: string }) {
  const { t } = useTranslation();
  const history = useFileContactHistory(fileId);
  const data = history.data;
  if (!data || !data.enabled) return null;

  return (
    <Box sx={{ mt: 2 }} aria-labelledby={`contact-history-${fileId}`}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <Icons.Phone size={iconSize.md} aria-hidden />
        <Typography
          id={`contact-history-${fileId}`}
          variant="subtitle2"
          component="h3"
          sx={{ fontWeight: 700 }}
        >
          {t('gov.frontDesk.history.title')}
        </Typography>
      </Stack>
      {data.linked.length === 0 && data.sameProperty.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('gov.frontDesk.history.empty')}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {data.linked.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('gov.frontDesk.history.linked', { count: data.linked.length })}
              </Typography>
              {data.linked.map((entry) => (
                <ContactEntrySummary key={entry.id} entry={entry} />
              ))}
            </Box>
          )}
          {data.sameProperty.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('gov.frontDesk.history.sameProperty', { count: data.sameProperty.length })}
              </Typography>
              {data.sameProperty.map((entry) => (
                <ContactEntrySummary key={entry.id} entry={entry} />
              ))}
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
}
